import { useTheme } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Section } from 'src/components/section';
import { ControlledField } from 'src/components/controlled-field';
import { Button } from 'src/components/button';
import { getTheme } from 'src/utils/getTheme';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning-hos.svg';
import { useRequestOtpSupabase } from 'src/auth/hooks/use-request-otp-supabase';
import { useVerifyOtpSupabase } from 'src/auth/hooks/use-verify-otp-supabase';
import { LoginSteps } from 'src/shared/enum/LoginSteps';
import type { LoginFormProps } from 'src/features/login-form/types';

import { loginOtpDataKeys } from '../constants';

import { styles } from './styles';

const RESEND_TIMEOUT = 60;

export const OtpLoginForm: FC<LoginFormProps> = ({ onClose, toggleLoginMode }) => {
    const [step, setStep] = useState<LoginSteps.EMAIL | LoginSteps.OTP>(LoginSteps.EMAIL);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [resendCountdown, setResendCountdown] = useState<number>(0);
    const [lastEmailSent, setLastEmailSent] = useState<string | null>(null);

    const theme = useTheme();
    const { t } = useTranslation();
    const { isHosTheme } = getTheme();

    const {
        isSuccess: successRequest,
        isPending: pendingRequest,
        isError: failedRequest,
        mutate: mutateRequest,
        error: requestError,
        reset: resetRequest,
    } = useRequestOtpSupabase();

    const {
        isSuccess: successVerify,
        isPending: pendingVerify,
        isError: failedVerify,
        mutate: mutateVerify,
        error: verifyError,
        reset: resetVerify,
    } = useVerifyOtpSupabase();

    const {
        control,
        watch,
        register,
        trigger,
        formState: { errors },
    } = useFormContext();

    useEffect(() => {
        if (resendCountdown <= 0) {
            return undefined;
        }

        const interval = setInterval(() => {
            setResendCountdown((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(interval);
    }, [resendCountdown]);

    const watchEmailField = watch('email');

    const emailHasValidationError = !!errors.email;

    useEffect(() => {
        if (watchEmailField !== lastEmailSent) {
            setResendCountdown(0);
        }
    }, [watchEmailField, lastEmailSent]);

    const getOtpErrorMessage = (): string | null => {
        if (emailHasValidationError) return null;

        if (step === LoginSteps.EMAIL && failedRequest && requestError) {
            switch (requestError.status) {
                case 422:
                    return t('supabase_otp_login_error_bad_email');
                case 429:
                    return t('supabase_password_login_error_rate_limit');
                default:
                    return t('supabase_otp_login_error_generic');
            }
        }

        if (step === LoginSteps.OTP && (requestError || verifyError)) {
            switch (requestError?.status || verifyError?.status) {
                case 403:
                    return t('supabase_otp_login_error_bad_code');
                case 429:
                    return t('supabase_password_login_error_rate_limit');
                default:
                    return t('supabase_otp_error_verify_generic');
            }
        }

        return null;
    };

    const otpInputs = otpDigits.map((digit, index) => ({
        id: `otp-${index}`,
        value: digit,
    }));

    const onSubmitEmail = async () => {
        const isValid = await trigger(loginOtpDataKeys.email);
        if (!isValid) return;

        resetVerify();
        mutateRequest(
            { email: watchEmailField },
            {
                onSuccess: () => {
                    setStep(LoginSteps.OTP);
                    setResendCountdown(RESEND_TIMEOUT);
                    setLastEmailSent(watchEmailField);
                },
            }
        );
    };

    const onSubmitOtp = () => {
        const token = otpDigits.join('');
        if (token.length === 6) {
            mutateVerify({ email: watchEmailField, token });
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;
        const newDigits = [...otpDigits];
        newDigits[index] = value;
        setOtpDigits(newDigits);

        if (value && index < 5) inputRefs.current[index + 1]?.focus();
        if (!value && index > 0) inputRefs.current[index - 1]?.focus();

        if (failedVerify) resetVerify();
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').trim();

        if (!/^\d+$/.test(pasted)) return;

        const digits = pasted.slice(0, 6).split('');
        const newDigits = ['', '', '', '', '', ''];

        digits.forEach((d, i) => {
            if (i < 6) newDigits[i] = d;
        });

        setOtpDigits(newDigits);

        if (failedVerify) resetVerify();

        const lastIndex = Math.min(digits.length - 1, 5);
        inputRefs.current[lastIndex]?.focus();
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            const currentValue = otpDigits[index];

            if (currentValue !== '') return;

            if (index > 0) {
                e.preventDefault();

                const prevIndex = index - 1;

                setOtpDigits((prev) => {
                    const updated = [...prev];
                    updated[prevIndex] = '';
                    return updated;
                });

                inputRefs.current[prevIndex]?.focus();
            }
        }
    };

    const handleSubmitOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (step === LoginSteps.EMAIL) {
                onSubmitEmail();
            } else {
                onSubmitOtp();
            }
        }
    };

    const handleResendCode = async () => {
        const isValid = await trigger(loginOtpDataKeys.email);
        if (!isValid) return;

        if (failedVerify || verifyError) {
            setOtpDigits(['', '', '', '', '', '']);
            resetVerify();
        }

        mutateRequest(
            { email: watchEmailField },
            {
                onSuccess: () => {
                    setResendCountdown(RESEND_TIMEOUT);
                    setLastEmailSent(watchEmailField);
                },
            }
        );
    };

    const handleBackToEmail = () => {
        resetVerify();
        resetRequest();
        setOtpDigits(['', '', '', '', '', '']);
        setStep(LoginSteps.EMAIL);
    };

    if (successVerify) {
        onClose();
        return null;
    }

    return (
        <Box css={styles.loginDialogWrapper}>
            <Box>
                {step === LoginSteps.EMAIL ? (
                    <LockIcon fontSize="large" />
                ) : (
                    <EmailIcon fontSize="large" />
                )}
            </Box>

            <Section
                header={
                    step === LoginSteps.EMAIL
                        ? t('login_dialog_otp_title')
                        : t('login_dialog_otp_title_verify_code')
                }
                withHeaderStipe={false}
            >
                {getOtpErrorMessage() && (
                    <Box css={styles.loginDialogErrorBox}>
                        {isHosTheme && <WarningIcon />}
                        <Typography color={theme.palette.primary.main} variant="subtitle1">
                            {getOtpErrorMessage()}
                        </Typography>
                    </Box>
                )}

                {step === LoginSteps.EMAIL && (
                    <Fragment>
                        <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                            {t('login_dialog_otp_enter_email')}
                        </Typography>

                        <Stack
                            spacing="20px"
                            css={styles.loginFormWrapper}
                            onKeyDown={handleSubmitOnEnter}
                        >
                            <Typography variant="subtitle4" sx={{ mb: 1 }}>
                                {t('login_dialog_email_address')}
                            </Typography>

                            <ControlledField
                                type="email"
                                {...register(loginOtpDataKeys.email)}
                                name={loginOtpDataKeys.email}
                                control={control}
                                placeholder={t('login_dialog_email_address')}
                                isLoginVariant
                                restStyles={styles.LoginInput}
                                id="login-otp-email"
                            />
                        </Stack>

                        <Button
                            disableElevation
                            css={styles.loginButton}
                            variant="contained"
                            onClick={onSubmitEmail}
                            loading={pendingRequest}
                            disabled={!watchEmailField}
                            id="login-otp-email-request"
                        >
                            {t('login_dialog_cta_send_code')}
                        </Button>
                    </Fragment>
                )}

                {step === LoginSteps.OTP && (
                    <Fragment>
                        {successRequest && (
                            <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                                <Trans
                                    i18nKey="login_dialog_otp_email_sent"
                                    values={{ email: watchEmailField }}
                                    components={{ strong: <strong /> }}
                                />
                            </Typography>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('login_dialog_check_spam')}
                        </Typography>

                        <Typography variant="subtitle4" sx={{ mb: 1 }}>
                            {t('login_dialog_supabase_otp_token')}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            sx={{ width: '100%' }}
                            onKeyDown={handleSubmitOnEnter}
                        >
                            {otpInputs.map((otp) => {
                                const index = Number(otp.id.split('-')[1]);

                                return (
                                    <input
                                        key={otp.id}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        aria-label={`Digit ${index + 1} of ${otpInputs.length}`}
                                        aria-required="true"
                                        aria-invalid={failedVerify ? 'true' : 'false'}
                                        value={otp.value}
                                        ref={(el) => {
                                            inputRefs.current[index] = el;
                                        }}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onPaste={handleOtpPaste}
                                        css={[
                                            styles.otpInput,
                                            failedVerify && styles.otpInputError,
                                        ]}
                                        id={`login-otp-verify-digit-${index}`}
                                    />
                                );
                            })}
                        </Stack>

                        <Button
                            disableElevation
                            css={styles.loginButton}
                            variant="contained"
                            onClick={onSubmitOtp}
                            loading={pendingVerify}
                            disabled={otpDigits.some((d) => d === '')}
                            sx={{ mt: 3 }}
                            id="login-otp-verify"
                        >
                            {t('login_dialog_cta_verify')}
                        </Button>
                    </Fragment>
                )}
            </Section>

            {step === LoginSteps.EMAIL ? (
                <Typography
                    css={styles.forgottenPasswordText}
                    sx={{ cursor: 'pointer', textAlign: 'center', mt: 1 }}
                    onClick={toggleLoginMode}
                    id="login-switch-email"
                >
                    {t('login_dialog_sign_in_with_password')}
                </Typography>
            ) : (
                <Stack spacing={1} sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography>{t('login_dialog_didnt_receive_code')}</Typography>
                    <Typography
                        variant="body1"
                        css={[styles.link, resendCountdown > 0 && styles.resendLinkDisabled]}
                        onClick={resendCountdown > 0 ? undefined : handleResendCode}
                        id="login-otp-request-resend"
                    >
                        {resendCountdown > 0
                            ? `${t('login_dialog_resend_code')} (in ${resendCountdown} seconds)`
                            : t('login_dialog_resend_code')}
                    </Typography>
                </Stack>
            )}

            <hr css={styles.separationLine} />
            {step === LoginSteps.OTP && (
                <Typography
                    sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                    css={styles.link}
                    onClick={handleBackToEmail}
                    id="login-otp-back"
                >
                    <ArrowBackIcon fontSize="small" sx={{ mr: 0.3 }} />
                    {t('login_dialog_back')}
                </Typography>
            )}
        </Box>
    );
};
